import { useMemo } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getPermitsCustomFields } from "../../../queries/permits_custom_fields";
import { getPermitsForm } from "../../../queries/permits_forms";
import network from "../../../selectors/network";

/**
 * Retrieves a permit form and returns its data in a usable format
 * @param permitFormId
 */
export function usePermitCustomFields(permitsFormId: string | number) {
  const networkHeaders = useSelector(network.defaultHeaders);
  const permitsFormQuery = useQuery(["permitsForms", permitsFormId], getPermitsForm(networkHeaders), {
    enabled: permitsFormId,
  });
  const customFieldsQuery = useQuery("permitsCustomFields", getPermitsCustomFields(networkHeaders));

  // Build a map of custom fields, mapped by custom field ID
  const allCustomFieldsMap = useMemo(() => {
    if (!customFieldsQuery.data) return {};

    const customFieldsList: { id: string; type: string; attributes: { [key: string]: any } }[] =
      customFieldsQuery.data.data;
    return customFieldsList.reduce(
      (prev, cur) => {
        prev[cur.id] = cur.attributes;
        return prev;
      },
      {} as { [key: string]: any },
    );
  }, [customFieldsQuery.data]);

  // Build a map of form fields, mapped by form field ID
  const formFieldList: { [key: string]: any }[] = permitsFormQuery.data?.included ?? [];
  const formFieldMap = formFieldList.reduce((prev, cur) => {
    if (cur.type === "permits-form-field") {
      prev[cur.id] = cur;
    }
    return prev;
  }, {});

  const formCustomFieldsMap = formFieldList.reduce((prev, cur) => {
    if (cur.type === "permits-form-field") {
      // Find the custom field that corresponds to this form field
      const permitsExtensionDefId = cur.attributes["permits-extension-def-id"];
      const customField = allCustomFieldsMap[permitsExtensionDefId] ?? null;
      prev[cur.id] = customField;
    }
    return prev;
  }, {});

  return {
    isLoading: permitsFormQuery.isLoading || customFieldsQuery.isLoading,
    formFieldList, // list of form fields
    formFieldMap, // map of form fields
    allCustomFieldsMap, // map of all custom fields
    formCustomFieldsMap, // map of custom fields that are associated with form fields (mapped by form field ID)
  };
}
